import events from 'events-bus';
import { PropsWithChildren, useEffect, useState, useContext, createContext } from 'react';

import { initAxios, syncToken } from '@services/api';
import { Maybe } from '@types-internal/index';

export type KeycloakTokenProviderProps = {
  allowedUrls?: string[];
  getTokenOverride?: ({
    setKeycloakTokenLoaded,
    setTokenPayload,
  }: {
    setKeycloakTokenLoaded: (loaded: boolean) => void;
    setTokenPayload: (payload: Maybe<KeycloakTokenContextData['tokenPayload']>) => void;
  }) => void;
};

export type KeycloakTokenContextData = {
  tokenPayload?: {
    sid?: string;
  };
};

export const KeycloakTokenContext = createContext<KeycloakTokenContextData>({
  tokenPayload: {
    sid: undefined,
  },
});
export const useKeycloakTokenProvider = () => useContext(KeycloakTokenContext);

const KeycloakTokenProvider: React.FC<PropsWithChildren<KeycloakTokenProviderProps>> = ({
  allowedUrls = [],
  children,
  getTokenOverride,
}) => {
  const [keycloakTokenLoaded, setKeycloakTokenLoaded] = useState(false);
  const [tokenPayload, setTokenPayload] =
    useState<Maybe<KeycloakTokenContextData['tokenPayload']>>(undefined);

  useEffect(() => {
    initAxios(allowedUrls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getToken = () => {
    if (getTokenOverride) {
      return getTokenOverride({ setKeycloakTokenLoaded, setTokenPayload });
    }
    const frontendAuthorizerConfig = (window as any).frontendAuthorizerConfig;
    if (frontendAuthorizerConfig?.tokenPayload?.customerId) {
      syncToken(frontendAuthorizerConfig.token);
      setKeycloakTokenLoaded(frontendAuthorizerConfig.token);
    }
    if (frontendAuthorizerConfig?.tokenPayload) {
      setTokenPayload(frontendAuthorizerConfig?.tokenPayload);
    }
  };

  useEffect(() => {
    getToken();

    const tokenListener = (_: any, data: { token: string; payload: { sid: string } }) => {
      const token = data.token;
      syncToken(token);
      setKeycloakTokenLoaded(!!token);
      if (data.payload) {
        setTokenPayload(data.payload);
      }
    };
    events.on('AUTH-SUCCESS-EVENT-v1', tokenListener);
    return () => {
      events.off(tokenListener);
    };
  }, []);

  if (!keycloakTokenLoaded) {
    return null;
  }

  return (
    <KeycloakTokenContext.Provider value={{ tokenPayload }}>
      {children}
    </KeycloakTokenContext.Provider>
  );
};

export default KeycloakTokenProvider;
